import type { ThemeState } from '@atlaskit/tokens/theme-config';

import { fg } from '@confluence/feature-gating';
import {
	API_DEFAULT_THEME_STATE,
	getEffectiveThemeState,
	getEffectiveThemeStateSerialized,
} from '@confluence/theming-utils/entry-points/getEffectiveTheme.ts';

import { ErrorSubjects, ErrorSubjectIds, reportThemingError } from './errorHandling.ts';
import type { ThemePreferenceSelectorQuery as ThemePreferenceSelectorQueryType } from './graphql/__types__/ThemePreferenceSelectorQuery.ts';
import type {
	ThemePreferenceMutation as ThemePreferenceMutationType,
	ThemePreferenceMutationVariables,
} from './graphql/__types__/ThemePreferenceMutation.ts';
import ThemePreferenceSelectorQuery from './graphql/__generated__/ThemePreferenceQuerySelectorQuery.graphql.ts';
import ThemePreferenceMutation from './graphql/__generated__/ThemePreferenceMutation.graphql.ts';
import { RemoteThemeStorageShared } from './remoteThemeStorageShared.ts';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
export class RemoteThemeStorage {
	public static async getThemeRemote(): Promise<Partial<ThemeState>> {
		if (fg('shared-user-preferences')) {
			return RemoteThemeStorageShared.getThemeRemote();
		}

		const { data, error } = await queryInterop<ThemePreferenceSelectorQueryType>({
			query: ThemePreferenceSelectorQuery,
		});

		if (error) {
			throw error;
		}

		return getEffectiveThemeState(data?.userPreferences?.theme);
	}

	public static async saveThemeRemote(
		theme: Partial<ThemeState> | typeof API_DEFAULT_THEME_STATE,
	): Promise<void> {
		if (fg('shared-user-preferences')) {
			// Convert "default" string to actual theme state before passing to shared implementation
			const themeState =
				theme === API_DEFAULT_THEME_STATE ? getEffectiveThemeState(API_DEFAULT_THEME_STATE) : theme;
			return RemoteThemeStorageShared.saveThemeRemote(themeState);
		}

		try {
			const { errors } = await mutateInterop<
				ThemePreferenceMutationType,
				ThemePreferenceMutationVariables
			>({
				mutation: ThemePreferenceMutation,
				variables: { theme: getEffectiveThemeStateSerialized(theme) },
			});

			if (errors) {
				const attributes = {
					errors:
						errors?.map((err) => ({
							message: err.message,
							stack: err.stack,
						})) || [],
					theme,
				};
				await reportThemingError(
					ErrorSubjects.remoteThemeStorage,
					ErrorSubjectIds.apolloMutationErrors,
					attributes,
				);
			}
		} catch (e) {
			const attributes = {
				message: e.message,
				stack: e.stack,
				theme,
			};
			await reportThemingError(
				ErrorSubjects.remoteThemeStorage,
				ErrorSubjectIds.saveThemeRemote,
				attributes,
			);
		}
	}
}
